// src/components/LeftSidebar/DeleteAllConversationsButton.tsx
import React, { useState } from "react";

import {
  Button,
  Dialog,
  DialogContent,
  Typography,
  Grid,
  IconButton,
  Box,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useMsal } from "@azure/msal-react";

import apiCall from "../../utils/apiCall";
import { DELETE_ALL_CONVERSATIONS } from "../../endpoint";

import { useAppDispatch, useAppSelector } from "../../hooks/reduxhook";
import {
  addConversations,
  clearActiveChat,
} from "../../redux/slices/conversationSlice";
import { loadingforconversation } from "../../redux/slices/loadingSlice";
import { addError } from "../../redux/slices/errorSlice";

interface Props {
  /**
   * Optional prop to override disabled state.
   * If not provided, component will check Redux to see whether conversations exist.
   */
  disabled?: boolean;
  /**
   * Optional small variant to make the button smaller if you want.
   */
  small?: boolean;
}

const DeleteAllConversationsButton: React.FC<Props> = ({ disabled: disabledProp, small }) => {
  const dispatch = useAppDispatch();
  const { accounts } = useMsal();
  const account = accounts && accounts.length > 0 ? accounts[0] : null;
  const [open, setOpen] = useState(false);

  // Attempt to find conversations in redux under a few common keys to be robust.
  // Adjust this selector to your actual store shape if you know the exact path.
  const conversationsFromStore = useAppSelector((state: any) => {
    return (
      // common shapes
      state.conversation?.conversations ??
      state.conversation?.items ??
      state.conversations ??
      state.chatSessions ??
      state.chatSessions?.items ??
      []
    );
  });

  const count = conversationsFromStore.length;

  const hasConversations = Array.isArray(conversationsFromStore) && count > 0;

  const disabled = typeof disabledProp === "boolean" ? disabledProp : !hasConversations;

  const handleDeleteAll = () => {
    if (!account?.username) {
      dispatch(addError("No account found. Please log in again."));
      return;
    }

    dispatch(loadingforconversation(true));

    apiCall("DELETE", DELETE_ALL_CONVERSATIONS, {
      user_email: account.username,
    })
      .then(() => {
        dispatch(addConversations([]));
        dispatch(clearActiveChat());
      })
      .catch(() => {
        dispatch(addError("Failed to delete all conversations. Please try again."));
      })
      .finally(() => {
        dispatch(loadingforconversation(false));
        setOpen(false);
      });
  };

  // prevent dialog open when disabled
  const openConfirm = () => {
    if (disabled) return;
    setOpen(true);
  };

  return (
    <>
      <Box
        sx={{
          display: "inline-block",
          // visually indicate disabled state and prevent interactions when no conversations
          opacity: disabled ? 0.56 : 1,
          pointerEvents: disabled ? "none" : "auto",
        }}
        aria-disabled={disabled}
        title={disabled ? "No conversations to delete" : "Delete all conversations"}
      >
        <Button
          id="delete-all-conversations-button"
          variant="contained"
          color="primary"
          onClick={openConfirm}
          sx={{
            margin: "0px auto 0px",
            mr: 1,
            px: 2, // horizontal padding so it looks nice
            fontSize: small ? 12 : 12.8,
            minWidth: "auto", // 🔑 ensures button shrinks/grows to text
            whiteSpace: "nowrap", // 🔑 keep text in one line
          }}
          disabled={disabled}
        >
          Delete ({count})
        </Button>
      </Box>

      <Dialog
        open={open}
        keepMounted
        onClose={() => setOpen(false)}
        aria-describedby="alert-dialog-slide-description"
        maxWidth={"sm"}
      >
        <IconButton
          aria-label="close"
          onClick={() => setOpen(false)}
          size="small"
          sx={{
            position: "absolute",
            right: 2,
            top: 2,
          }}
        >
          <Close sx={{ color: "gray" }} />
        </IconButton>
        <DialogContent sx={{ p: 4 }}>
          <Grid container direction="column">
            <Typography
              align="center"
              sx={{
                mb: 3,
              }}
              color="primary"
            >
              Are you sure you want to delete all conversations?
            </Typography>
            <Grid
              direction="row"
              container
              spacing={2}
              sx={{
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Grid item>
                <Button
                  data-testid="yes"
                  variant="contained"
                  color="primary"
                  onClick={handleDeleteAll}
                  sx={{ padding: "4px 10px" }}
                >
                  Yes
                </Button>
              </Grid>
              <Grid item>
                <Button
                  color="primary"
                  variant="outlined"
                  onClick={() => setOpen(false)}
                  sx={{ padding: "4px 10px" }}
                >
                  No
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DeleteAllConversationsButton;
